import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';

interface StockReportItem {
  serialNumber: number;
  branch: string;
  loanNumber: string;
  loanDate: string | null;
  grossWeight: number;
  netWeight: number;
  value: number;
  totalValue: number;
  holdDays: number;
}

interface StockReportProps {
  data: StockReportItem[];
}

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const columns: ColumnsType<StockReportItem> = [
  {
    title: 'S.No',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 60,
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'Loan Date',
    dataIndex: 'loanDate',
    key: 'loanDate',
    render: formatDate,
  },
  {
    title: 'Gross Weight (g)',
    dataIndex: 'grossWeight',
    key: 'grossWeight',
    render: (val: number) => `${val.toFixed(3)} g`,
  },
  {
    title: 'Net Weight (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number) => `${val.toFixed(3)} g`,
  },
  {
    title: 'Value (₹/g)',
    dataIndex: 'value',
    key: 'value',
    render: (val: number) => `₹${val.toLocaleString('en-IN')}`,
  },
  {
    title: 'Total Value (₹)',
    dataIndex: 'totalValue',
    key: 'totalValue',
    render: (val: number) => `₹${val.toLocaleString('en-IN')}`,
  },
  {
    title: 'Hold Days',
    dataIndex: 'holdDays',
    key: 'holdDays',
  },
];

const StockReportDisplay: React.FC<StockReportProps> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          grossWeight: acc.grossWeight + (item.grossWeight ?? 0),
          netWeight: acc.netWeight + (item.netWeight ?? 0),
          totalValue: acc.totalValue + (item.totalValue ?? 0),
        }),
        { grossWeight: 0, netWeight: 0, totalValue: 0 },
      ),
    [data],
  );

  return (
    <div className="loan-data-container">
      <h2>Stock Report</h2>
      <Table
        columns={columns}
        dataSource={data.map((item) => ({ ...item, key: `${item.loanNumber}-${item.serialNumber}` }))}
        rowKey="key"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <strong>{totals.grossWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <strong>{totals.netWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>₹{totals.totalValue.toLocaleString('en-IN')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8}>-</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default StockReportDisplay;
